import {
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AttachMoney as MoneyIcon,
  DirectionsCar as CarIcon,
  LocalCarWash as WashIcon,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateCarWash } from "../../../../services/carWash.service";
import { ICarWashObject } from "../../../../types/carWash";
import { useCarWash } from "../../../../context/CarWashContext";

type Props = {};

const vehicleTypes = [
  { key: "sedan", labelKey: "vehicle.sedan", icon: "🚗" },
  { key: "cuv", labelKey: "vehicle.cuv", icon: "🚙" },
  { key: "suv", labelKey: "vehicle.suv", icon: "🚐" },
  { key: "minivan", labelKey: "vehicle.minivan", icon: "🚐" },
  { key: "van", labelKey: "vehicle.van", icon: "🚐" },
];

const serviceTypes = [
  {
    key: "inner",
    labelKey: "vehicle.innerWash",
    descriptionKey: "vehicle.innerWashDesc",
  },
  {
    key: "outer",
    labelKey: "vehicle.outerWash",
    descriptionKey: "vehicle.outerWashDesc",
  },
  {
    key: "both",
    labelKey: "vehicle.fullWash",
    descriptionKey: "vehicle.fullWashDesc",
  },
];

const Prices = (props: Props) => {
  const { t } = useTranslation();
  const { carWash, loading: carWashLoading, refetch } = useCarWash();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<ICarWashObject | null>(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  useEffect(() => {
    if (carWash) {
      setEditedData(carWash);
    }
  }, [carWash]);

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset to original data
      setEditedData(carWash);
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!editedData || !carWash?._id) return;

    setLoading(true);
    try {
      await updateCarWash(carWash._id, {
        services: editedData.services,
      });
      await refetch();
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: t("prices.updatedSuccess"),
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || t("prices.updateFailed"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (
    vehicleType: string,
    serviceType: string,
    value: number
  ) => {
    setEditedData((prev: any) => ({
      ...prev,
      services: {
        ...prev.services,
        [vehicleType]: {
          ...prev.services[vehicleType],
          [serviceType]: value,
        },
      },
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getAverageVehiclePrice = (vehicleType: string) => {
    if (
      !carWashInfo?.services[vehicleType as keyof typeof carWashInfo.services]
    )
      return 0;
    const services =
      carWashInfo.services[vehicleType as keyof typeof carWashInfo.services];
    return (services.inner + services.outer + services.both) / 3;
  };

  const getAveragePrice = () => {
    if (!carWashInfo?.services) return 0;
    const totals = vehicleTypes.map((type) => getAverageVehiclePrice(type.key));
    return totals.reduce((sum, total) => sum + total, 0) / totals.length;
  };

  if (carWashLoading || !carWash) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography>{t("prices.loading")}</Typography>
      </Box>
    );
  }

  const carWashInfo = carWash;

  return (
    <Box p={3} borderRadius={2} boxShadow={3} bgcolor="background.paper">
      {/* Header with Edit Toggle */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <MoneyIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h5" gutterBottom>
            {t("carWashDetails.servicePrices")}
          </Typography>
        </Box>
        <Box>
          {isEditing ? (
            <>
              <IconButton
                onClick={handleSave}
                disabled={loading}
                color="primary"
                title={t("common.saveChanges")}
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                onClick={handleEditToggle}
                disabled={loading}
                color="secondary"
                title={t("common.cancelEditing")}
              >
                <CancelIcon />
              </IconButton>
            </>
          ) : (
            <IconButton
              onClick={handleEditToggle}
              color="primary"
              title={t("carWashes.editPrices")}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h6" color="primary">
                ₼ {getAveragePrice().toFixed(2)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t("prices.averagePrice")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h6" color="primary">
                {carWashInfo?.legalName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t("prices.carWashName")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Prices Table */}
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <WashIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">{t("prices.servicePricing")}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <CarIcon sx={{ mr: 1 }} />
                      {t("vehicleTypeReviews.vehicleType")}
                    </Box>
                  </TableCell>
                  {serviceTypes.map((service) => (
                    <TableCell key={service.key} align="center">
                      <Box textAlign="center">
                        <Typography variant="subtitle2">
                          {t(service.labelKey)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t(service.descriptionKey)}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicleTypes.map((vehicle) => (
                  <TableRow key={vehicle.key}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Typography variant="h6" sx={{ mr: 1 }}>
                          {vehicle.icon}
                        </Typography>
                        <Box>
                          <Typography variant="subtitle1">
                            {t(vehicle.labelKey)}
                          </Typography>
                          <Chip
                            label={`₼ ${getAverageVehiclePrice(
                              vehicle.key
                            ).toFixed(2)}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    {serviceTypes.map((service) => (
                      <TableCell key={service.key} align="center">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={
                              editedData?.services[
                                vehicle.key as keyof typeof editedData.services
                              ]?.[
                                service.key as keyof typeof editedData.services.sedan
                              ] || 0
                            }
                            onChange={(e) =>
                              handlePriceChange(
                                vehicle.key,
                                service.key,
                                Number(e.target.value)
                              )
                            }
                            InputProps={{
                              startAdornment: (
                                <Typography variant="caption">₼</Typography>
                              ),
                            }}
                            sx={{ width: 80 }}
                          />
                        ) : (
                          <Typography variant="h6" color="primary">
                            ₼{" "}
                            {carWashInfo?.services[
                              vehicle.key as keyof typeof carWashInfo.services
                            ]?.[
                              service.key as keyof typeof carWashInfo.services.sedan
                            ] || 0}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Washing classes are now auto-computed from per-combination price ranges (Phase 24). */}

      {/* Additional Information */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("prices.pricingInformation")}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid size={6}>
              <Typography variant="body2" color="textSecondary">
                <strong>{t("ownerInfo.lastUpdated")}:</strong>{" "}
                {new Date(carWashInfo?.createdAt || "").toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="body2" color="textSecondary">
                <strong>{t("prices.totalServices")}:</strong>{" "}
                {vehicleTypes.length * serviceTypes.length}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="body2" color="textSecondary">
                <strong>{t("common.note")}:</strong>{" "}
                {t("prices.pricingNote")}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Prices;
